import { GRID_COLS, GRID_ROWS, CELL, PLAYER_SPAWN, POWERUP_POS, TUNNEL_ROW } from './config';
import { validateTileTypes } from './tileTypes';

// ── Pac-Man map templates (21×27, left-right symmetric) ──
//
// Grid: 21 columns × 27 rows.  Centre axis = column 10.
// Corridor width = 1 tile.  Rules:
//  - Every walkable tile connects to >= 2 walkable neighbours (no dead ends)
//  - No 2×2 block of walkable cells
//  - Left-right symmetric around column 10
//  - All walkable tiles in one connected network
//  - Hub at cols 8-12 rows 13-15, tunnel at row 14

// ── Row patterns ──
const BORDER   = [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1];
const BORDER_V = [1,1,1,1,1,1,1,1,1,1,4,1,1,1,1,1,1,1,1,1,1]; // vertical tunnel at col 10
const R_PU     = [1,3,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,3,1];
const FULL     = [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1];
const ABOVE    = [1,1,1,1,1,0,0,0,0,0,0,0,0,0,0,0,1,1,1,1,1];
const HUB_T    = [1,1,1,1,1,0,1,0,1,2,2,2,1,0,1,0,1,1,1,1,1];
const HUB_TN   = [4,0,0,0,0,0,1,0,2,2,2,2,2,0,1,0,0,0,0,0,4];
const HUB_M    = [1,1,1,1,1,0,1,0,2,2,2,2,2,0,1,0,1,1,1,1,1];
const HUB_B    = [1,1,1,1,1,0,1,0,1,1,1,1,1,0,1,0,1,1,1,1,1];

// Classic patterns (corridors spaced ≥2 apart, symmetric)
const PA  = [1,0,1,1,1,0,1,1,1,0,1,0,1,1,1,0,1,1,1,0,1]; // {1,5,9,11,15,19}
const PB  = [1,0,1,1,1,0,1,0,1,1,1,1,1,0,1,0,1,1,1,0,1]; // {1,5,7,13,15,19}

// Vertical corridor: col 10 always open
const V1  = [1,0,1,1,0,1,1,0,1,1,0,1,1,0,1,1,0,1,1,0,1]; // {1,4,7,10,13,16,19}
const V2  = [1,0,1,1,1,0,1,1,1,1,0,1,1,1,1,0,1,1,1,0,1]; // {1,5,10,15,19}
const SV  = [1,1,1,1,1,0,1,1,1,1,0,1,1,1,1,0,1,1,1,1,1]; // SIDE with vert {5,10,15}

// Dense grid: many corridors, small blocks
const D1  = [1,0,1,0,1,0,1,1,1,0,1,0,1,1,1,0,1,0,1,0,1]; // {1,3,5,9,11,15,17,19}
const D2  = [1,0,1,0,1,1,0,1,0,1,1,1,0,1,0,1,1,0,1,0,1]; // {1,3,6,8,12,14,17,19}
const SD  = [1,1,1,1,1,0,1,0,1,0,1,0,1,0,1,0,1,1,1,1,1]; // SIDE dense {5,7,9,11,13,15}

const SIDE = [1,1,1,1,1,0,1,1,1,0,1,0,1,1,1,0,1,1,1,1,1]; // Classic {5,9,11,15}

// ── Templates ──
export const TEMPLATES = [
  // Template 1 — Classic Pac-Man layout
  [BORDER, R_PU, PA, PA, PA, FULL, PB, PB, FULL,
   SIDE, SIDE, SIDE, ABOVE, HUB_T, HUB_TN, HUB_M, HUB_B, ABOVE,
   SIDE, SIDE, SIDE, FULL, PB, PB, PB, R_PU, BORDER],

  // Template 2 — Vertical corridor at col 10 + different patterns + vertical tunnels
  [BORDER_V, R_PU, V1, V1, V1, FULL, V2, V2, FULL,
   SV, SV, SV, ABOVE, HUB_T, HUB_TN, HUB_M, HUB_B, ABOVE,
   SV, SV, SV, FULL, V2, V1, V1, R_PU, BORDER_V],

  // Template 3 — Dense grid with many passages + small wall blocks
  [BORDER, R_PU, D1, D1, D1, FULL, D2, D2, FULL,
   SD, SD, SD, ABOVE, HUB_T, HUB_TN, HUB_M, HUB_B, ABOVE,
   SD, SD, SD, FULL, D2, D1, D1, R_PU, BORDER],
];

// ── Validation ──

function isWalkable(t) {
  return t === CELL.CORRIDOR || t === CELL.POWERUP || t === CELL.TUNNEL;
}

function countNeighbours(grid, c, r) {
  let n = 0;
  if (c > 0              && isWalkable(grid[r][c - 1])) n++;
  if (c < GRID_COLS - 1  && isWalkable(grid[r][c + 1])) n++;
  if (r > 0              && isWalkable(grid[r - 1][c])) n++;
  if (r < GRID_ROWS - 1  && isWalkable(grid[r + 1][c])) n++;
  return n;
}

function validate(grid, idx) {
  // 1. No 2×2 walkable blocks
  for (let r = 0; r < GRID_ROWS - 1; r++) {
    for (let c = 0; c < GRID_COLS - 1; c++) {
      if (isWalkable(grid[r][c]) && isWalkable(grid[r][c + 1]) &&
          isWalkable(grid[r + 1][c]) && isWalkable(grid[r + 1][c + 1])) {
        return false;
      }
    }
  }

  // 2. No dead ends (tunnel endpoints are exempt — they warp)
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) {
      if (!isWalkable(grid[r][c])) continue;
      // Horizontal tunnel endpoints
      if (r === TUNNEL_ROW && (c === 0 || c === GRID_COLS - 1)) continue;
      // Vertical tunnel endpoints
      if ((r === 0 || r === GRID_ROWS - 1) && grid[r][c] === CELL.TUNNEL) continue;
      const n = countNeighbours(grid, c, r);
      if (n < 2) return false;
    }
  }

  // 3. All walkable cells connected (BFS from player spawn)
  const visited = new Set();
  const queue = [{ col: PLAYER_SPAWN.col, row: PLAYER_SPAWN.row }];
  visited.add(`${PLAYER_SPAWN.col},${PLAYER_SPAWN.row}`);
  while (queue.length) {
    const { col, row } = queue.shift();
    const dirs = [[0,-1],[0,1],[-1,0],[1,0]];
    // Add tunnel warp connections
    if (row === TUNNEL_ROW && col === 0) dirs.push([GRID_COLS - 1, 0]);
    if (row === TUNNEL_ROW && col === GRID_COLS - 1) dirs.push([-(GRID_COLS - 1), 0]);
    if (row === 0 && grid[0][col] === CELL.TUNNEL) dirs.push([0, GRID_ROWS - 1]);
    if (row === GRID_ROWS - 1 && grid[GRID_ROWS - 1][col] === CELL.TUNNEL) dirs.push([0, -(GRID_ROWS - 1)]);

    for (const [dc, dr] of dirs) {
      const nc = col + dc, nr = row + dr;
      const k = `${nc},${nr}`;
      if (nc >= 0 && nc < GRID_COLS && nr >= 0 && nr < GRID_ROWS &&
          !visited.has(k) && isWalkable(grid[nr][nc])) {
        visited.add(k);
        queue.push({ col: nc, row: nr });
      }
    }
  }
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) {
      if (isWalkable(grid[r][c]) && !visited.has(`${c},${r}`)) return false;
    }
  }

  // 4. Powerup positions
  for (const [c, r] of POWERUP_POS) {
    if (grid[r][c] !== CELL.POWERUP) return false;
  }

  // 5. Player spawn walkable
  if (!isWalkable(grid[PLAYER_SPAWN.row][PLAYER_SPAWN.col])) return false;

  // 6. Tile type validation
  if (!validateTileTypes(grid, idx)) return false;

  return true;
}

// ── Random Map Generator ──

function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function isWalk(grid, c, r) {
  if (c < 0 || c >= 21 || r < 0 || r >= 27) return false;
  const t = grid[r][c];
  return t === 0 || t === 3 || t === 4;
}

function countNeighbors(grid, c, r) {
  let n = 0;
  if (isWalk(grid, c, r-1)) n++;
  if (isWalk(grid, c, r+1)) n++;
  if (isWalk(grid, c-1, r)) n++;
  if (isWalk(grid, c+1, r)) n++;
  return n;
}

// Check if opening cell (c,r) creates any 2x2 walkable block
function creates2x2(grid, c, r) {
  for (const [dc, dr] of [[0,0],[-1,0],[0,-1],[-1,-1]]) {
    const cc = c + dc, rr = r + dr;
    if (cc < 0 || cc + 1 >= 21 || rr < 0 || rr + 1 >= 27) continue;
    if (isWalk(grid, cc, rr) && isWalk(grid, cc+1, rr) &&
        isWalk(grid, cc, rr+1) && isWalk(grid, cc+1, rr+1)) return true;
  }
  return false;
}

// Repair dead ends by opening a neighbor wall, or closing the dead end
function repairDeadEnds(grid) {
  let changed = true;
  let passes = 0;
  while (changed && passes < 50) {
    changed = false;
    passes++;
    for (let r = 1; r < 26; r++) {
      if (r >= 13 && r <= 16) continue;
      for (let c = 1; c < 20; c++) {
        if (!isWalk(grid, c, r)) continue;
        if (countNeighbors(grid, c, r) >= 2) continue;

        const dirs = shuffle([[0,-1],[0,1],[-1,0],[1,0]]);
        let fixed = false;
        for (const [dc, dr] of dirs) {
          const nc = c + dc, nr = r + dr;
          if (nc < 1 || nc > 19 || nr < 1 || nr > 25) continue;
          if (nr >= 13 && nr <= 16) continue;
          if (isWalk(grid, nc, nr)) continue;

          grid[nr][nc] = 0;
          const mc = 20 - nc;
          const prevMirror = grid[nr][mc];
          grid[nr][mc] = 0;

          // Accept only if ≥2 neighbors AND no 2x2 block created
          if (countNeighbors(grid, nc, nr) >= 2 &&
              !creates2x2(grid, nc, nr) && !creates2x2(grid, mc, nr)) {
            fixed = true;
            changed = true;
            break;
          } else {
            grid[nr][nc] = 1;
            grid[nr][mc] = prevMirror;
          }
        }
        if (!fixed && countNeighbors(grid, c, r) < 2) {
          grid[r][c] = 1;
          grid[r][20 - c] = 1;
          changed = true;
        }
      }
    }
  }
}

// Generate a random symmetric row with configurable corridor count
function randRow(mustOpen = [], minCorridors = 2, maxCorridors = 5, centerProb = 0.4) {
  const p = new Array(21).fill(1);
  p[0] = 1; p[20] = 1;
  for (const c of mustOpen) {
    if (c >= 1 && c <= 9) { p[c] = 0; p[20 - c] = 0; }
    if (c === 10) p[10] = 0;
  }
  const candidates = shuffle([1,2,3,4,5,6,7,8,9].filter(c => p[c] !== 0));
  const target = minCorridors + Math.floor(Math.random() * (maxCorridors - minCorridors + 1));
  let placed = 0;
  for (const c of candidates) {
    if (placed >= target) break;
    if (p[c] === 0) continue;
    if (c > 0 && p[c - 1] === 0) continue;
    if (c < 20 && p[c + 1] === 0) continue;
    p[c] = 0;
    p[20 - c] = 0;
    placed++;
  }
  if (!mustOpen.includes(10) && p[9] !== 0 && p[11] !== 0 && Math.random() < centerProb) p[10] = 0;
  return p;
}

// Generate a symmetric separator row: no two adjacent corridors (safe between FULL rows)
function separatorRow() {
  const p = new Array(21).fill(1);
  p[0] = 1; p[20] = 1;
  // Generate left half (cols 1-9), mirror to right half
  const cols = shuffle([1,2,3,4,5,6,7,8,9]);
  for (const c of cols) {
    const mc = 20 - c;
    if (c > 1 && p[c - 1] === 0) continue;  // no adjacent corridor to left
    if (p[c + 1] === 0) continue;            // no adjacent corridor to right
    if (Math.random() < 0.65) {              // 65% chance to open
      p[c] = 0;
      p[mc] = 0;
    }
  }
  // Center column
  if (p[9] !== 0 && p[11] !== 0 && Math.random() < 0.5) p[10] = 0;
  return p;
}

// Build a SIMPLE map: alternating FULL rows + separator rows = wide open layout
// Boundary constraint: rows 2,11 (adj to R_PU/ABOVE top) and 18,24 (adj to ABOVE/R_PU bottom)
// must be separator rows to avoid 2x2 walkable blocks
function buildSimpleGrid() {
  const hasVertical = Math.random() < 0.5;
  const borderRow = hasVertical ? [...BORDER_V] : [...BORDER];

  const grid = [];
  grid.push([...borderRow]);  // 0
  grid.push([...R_PU]);       // 1

  // Rows 2-11: FULL on odd rows (3,5,7,9), separator on even + boundaries
  for (let r = 2; r <= 11; r++) {
    if (r === 2 || r === 11 || r % 2 === 0) {
      grid.push(separatorRow());
    } else {
      grid.push([...FULL]);
    }
  }

  // Hub zone (fixed)
  grid.push([...ABOVE]);   // 12
  grid.push([...HUB_T]);   // 13
  grid.push([...HUB_TN]);  // 14
  grid.push([...HUB_M]);   // 15
  grid.push([...HUB_B]);   // 16
  grid.push([...ABOVE]);   // 17

  // Rows 18-24: FULL on odd rows (19,21,23), separator on even + boundaries
  for (let r = 18; r <= 24; r++) {
    if (r === 18 || r === 24 || r % 2 === 0) {
      grid.push(separatorRow());
    } else {
      grid.push([...FULL]);
    }
  }

  grid.push([...R_PU]);       // 25
  grid.push([...borderRow]);  // 26

  repairDeadEnds(grid);
  return grid;
}

// Build a MEDIUM map: moderate corridors, current approach
function buildMediumGrid() {
  const hasVertical = Math.random() < 0.5;
  const borderRow = hasVertical ? [...BORDER_V] : [...BORDER];
  const spinePool = shuffle([1,2,3,4,5,6,7,8,9]);
  const spines = [];
  for (const c of spinePool) {
    if (spines.length >= 3) break;
    if (spines.some(s => Math.abs(s - c) <= 1)) continue;
    spines.push(c);
  }
  if (hasVertical) spines.push(10);

  const fullPool = shuffle([3,4,5,6,7,8,9,10,19,20,21,22,23]);
  const fullRows = [];
  for (const r of fullPool) {
    if (fullRows.length >= 3) break;
    if (fullRows.some(f => Math.abs(f - r) <= 1)) continue;
    fullRows.push(r);
  }
  const fullSet = new Set(fullRows);

  const grid = [];
  grid.push([...borderRow]);
  grid.push([...R_PU]);
  for (let r = 2; r <= 11; r++) {
    grid.push(fullSet.has(r) ? [...FULL] : randRow(spines, 2, 5, 0.4));
  }
  grid.push([...ABOVE]);
  grid.push([...HUB_T]);
  grid.push([...HUB_TN]);
  grid.push([...HUB_M]);
  grid.push([...HUB_B]);
  grid.push([...ABOVE]);
  for (let r = 18; r <= 24; r++) {
    grid.push(fullSet.has(r) ? [...FULL] : randRow(spines, 2, 5, 0.4));
  }
  grid.push([...R_PU]);
  grid.push([...borderRow]);
  repairDeadEnds(grid);
  return grid;
}

// Build a COMPLEX map: tight corridors, dense maze
function buildComplexGrid() {
  const hasVertical = Math.random() < 0.5;
  const borderRow = hasVertical ? [...BORDER_V] : [...BORDER];
  const spinePool = shuffle([1,2,3,4,5,6,7,8,9]);
  const spines = [];
  for (const c of spinePool) {
    if (spines.length >= 2) break;
    if (spines.some(s => Math.abs(s - c) <= 1)) continue;
    spines.push(c);
  }
  if (hasVertical) spines.push(10);

  const fullPool = shuffle([3,4,5,6,7,8,9,10,19,20,21,22,23]);
  const fullRows = [];
  for (const r of fullPool) {
    if (fullRows.length >= 2) break;
    if (fullRows.some(f => Math.abs(f - r) <= 1)) continue;
    fullRows.push(r);
  }
  const fullSet = new Set(fullRows);

  const grid = [];
  grid.push([...borderRow]);
  grid.push([...R_PU]);
  for (let r = 2; r <= 11; r++) {
    grid.push(fullSet.has(r) ? [...FULL] : randRow(spines, 1, 3, 0.2));
  }
  grid.push([...ABOVE]);
  grid.push([...HUB_T]);
  grid.push([...HUB_TN]);
  grid.push([...HUB_M]);
  grid.push([...HUB_B]);
  grid.push([...ABOVE]);
  for (let r = 18; r <= 24; r++) {
    grid.push(fullSet.has(r) ? [...FULL] : randRow(spines, 1, 3, 0.2));
  }
  grid.push([...R_PU]);
  grid.push([...borderRow]);
  repairDeadEnds(grid);
  return grid;
}

export function generateRandomGrid() {
  const complexities = ['simple', 'medium', 'complex'];
  const complexity = complexities[Math.floor(Math.random() * complexities.length)];
  const builders = { simple: buildSimpleGrid, medium: buildMediumGrid, complex: buildComplexGrid };
  const build = builders[complexity];
  for (let i = 0; i < 300; i++) {
    const grid = build();
    if (validate(grid, 'rand')) {
      console.log(`Random map OK — ${complexity} (attempt ${i + 1})`);
      return grid;
    }
  }
  // Fallback: try any complexity
  for (const fallback of complexities) {
    const fb = builders[fallback];
    for (let i = 0; i < 100; i++) {
      const grid = fb();
      if (validate(grid, 'rand')) {
        console.log(`Random map OK — ${fallback} fallback (attempt ${i + 1})`);
        return grid;
      }
    }
  }
  console.warn('Random generation failed, using template 1');
  return TEMPLATES[0].map(r => [...r]);
}

// ── Public API ──

export function generateMap() {
  const order = [...Array(TEMPLATES.length).keys()].sort(() => Math.random() - 0.5);
  for (const i of order) {
    const grid = TEMPLATES[i].map(r => [...r]);
    if (validate(grid, i + 1)) {
      console.log(`Map: template ${i + 1} — OK`);
      return { grid, templateIndex: i };
    }
  }
  console.warn('Map: all templates failed, using template 1');
  return { grid: TEMPLATES[0].map(r => [...r]), templateIndex: 0 };
}
